import { useState } from 'react';
import {
  Button,
  DataTable,
  ErrorState,
  LoadingState,
  Modal,
  NotAvailable,
  Pager,
  Select,
  SelectableChip,
  Stat,
  StatusPill,
  type DataTableColumn,
} from '../../components';
import { formatCents, formatCount } from '../../lib/format/money';
import { formatDate } from '../../lib/format/dates';
import { notify } from '../../services/notify';
import { useLocalesById } from '../locales/queries';
import { localeLabel } from '../locales/api';
import { AdminNotesPanel } from '../profiles/AdminNotesPanel';
import { DeactivateProfileButton } from '../profiles/DeactivateProfileButton';
import { DemoProfileButton } from '../profiles/DemoProfileButton';
import { ReviewsReceived } from '../profiles/ReviewsReceived';
import { UserMessagePanel } from '../profiles/UserMessagePanel';
import { useDetailLinks } from '../orders/DetailDialogs';
import { BLUE_STAR_STATUSES, type BlueStarStatus, type ChefListing, type ProfileDetail } from '../profiles/api';
import { useChefListings, useProfileDetail, useSetBlueStarStatus } from '../profiles/queries';
import { useSession } from '../../lib/auth/useSession';
import {
  ACCOUNT_STATUS_TONE,
  BLUE_STAR_LABEL,
  BLUE_STAR_TONE,
  CHEF_STATUS_LABEL,
  CHEF_STATUS_TONE,
  ProfileHeader,
  RatingCell,
  SectionLabel,
  daysAgo,
  dishStatus,
  humanize,
} from '../profiles/people';

export const CHEF_TABS = ['profile', 'dishes', 'points', 'kit', 'notes', 'message'] as const;
export type ChefTab = (typeof CHEF_TABS)[number];
const TAB_LABEL: Record<ChefTab, string> = {
  profile: 'Profile',
  points: 'Points',
  dishes: 'Dishes',
  kit: 'Kit & containers',
  notes: 'Notes',
  message: 'Message',
};

/**
 * Chef profile dialog (kit `ChefProfileModal`, xl, 5 tabs, plus Message for
 * `user.message`). URL-addressable: the screen holds `?chef=<id>&chefTab=<tab>`.
 * Profile, Dishes, Notes and Message are real; Points and Kit are not built on
 * the BE and say so.
 *
 * Locale is read-only by design (FE plan §4.1): a profile's current locale is
 * not an admin setting, and each listing carries its own.
 */
export function ChefDialog({
  id,
  tab,
  onTab,
  onClose,
}: {
  id: string;
  tab: ChefTab;
  onTab: (tab: ChefTab) => void;
  onClose: () => void;
}) {
  const detail = useProfileDetail(id);
  const blueStar = useSetBlueStarStatus(id);
  const { locked, can } = useSession();
  const d = detail.data;
  // Message is hidden, not disabled, without user.message; a ?chefTab=message
  // link without it opens Profile.
  const tabs: readonly ChefTab[] = can('user.message') ? CHEF_TABS : CHEF_TABS.filter((t) => t !== 'message');
  const shown = tabs.includes(tab) ? tab : 'profile';

  return (
    <Modal isOpen onClose={onClose} size="xl">
      {detail.isPending ? (
        <LoadingState label="Loading chef…" />
      ) : detail.isError || !d ? (
        <ErrorState error={detail.error} onRetry={() => void detail.refetch()} />
      ) : (
        <>
          <Header detail={d} />

          <div className="mb-[18px] flex flex-wrap gap-[7px]" role="tablist" aria-label="Chef profile sections">
            {tabs.map((t) => (
              <SelectableChip
                key={t}
                role="tab"
                aria-selected={shown === t}
                active={shown === t}
                onClick={() => onTab(t)}
              >
                {TAB_LABEL[t]}
              </SelectableChip>
            ))}
          </div>

          <div role="tabpanel" aria-label={TAB_LABEL[shown]}>
            {shown === 'profile' && <ProfileTab detail={d} />}
            {shown === 'points' && (
              <NotAvailable title="Points" reason="Points are mocked in phase 1 and arrive with the Points screen." />
            )}
            {shown === 'dishes' && <DishesTab id={id} />}
            {shown === 'kit' && (
              <NotAvailable title="Kit & containers" reason="The chef kit is on the backlog; there is no data yet." />
            )}
            {shown === 'notes' && <AdminNotesPanel profileId={id} notes={d.adminNotes} noun="chef" />}
            {shown === 'message' && <UserMessagePanel profile={d.profile} noun="chef" />}
          </div>

          <div className="mt-[18px] flex items-center justify-between gap-2.5 border-t border-[rgba(143,68,32,.1)] pt-4">
            <div className="min-w-0 text-xs text-muted">Changes apply to the consumer app immediately.</div>
            <div className="flex shrink-0 gap-2 whitespace-nowrap">
              <Button variant="ghost-dark" size="sm" onClick={onClose}>
                Close
              </Button>
              {d.profile.blueStarStatus === 'pending' && (
                <Button
                  variant="ghost-dark"
                  size="sm"
                  loading={blueStar.isPending}
                  lockedReason={locked('crm.write')}
                  onClick={() =>
                    blueStar.mutate('approved', {
                      onSuccess: () => {
                        notify.success('Blue Star certification approved.');
                        onClose();
                      },
                    })
                  }
                >
                  Approve Blue Star
                </Button>
              )}
              <DeactivateProfileButton profileId={id} status={d.profile.status} noun="chef" onDone={onClose} />
              {shown === 'notes' && <DemoProfileButton profileId={id} demo={!!d.profile.demo} noun="chef" />}
            </div>
          </div>
        </>
      )}
    </Modal>
  );
}

function Header({ detail }: { detail: ProfileDetail }) {
  const { profile, computed } = detail;
  return (
    <ProfileHeader
      name={profile.displayName}
      avatarUrl={profile.avatarUrl}
      detail={
        <>
          <div>Joined {formatDate(profile.createdAt)}</div>
          <div className="mt-0.5 font-mono text-xs">{profile.id}</div>
        </>
      }
      pills={
        <>
          {computed && (
            <StatusPill tone={CHEF_STATUS_TONE[computed.status]} dot>
              {CHEF_STATUS_LABEL[computed.status]}
            </StatusPill>
          )}
          <StatusPill tone={BLUE_STAR_TONE[profile.blueStarStatus]}>
            Blue Star · {BLUE_STAR_LABEL[profile.blueStarStatus]}
          </StatusPill>
          {profile.status !== 'active' && (
            <StatusPill tone={ACCOUNT_STATUS_TONE[profile.status] ?? 'neutral'}>
              Account · {humanize(profile.status)}
            </StatusPill>
          )}
        </>
      }
    />
  );
}

function ProfileTab({ detail }: { detail: ProfileDetail }) {
  const { profile, computed } = detail;
  const setBlueStar = useSetBlueStarStatus(profile.id);
  const lock = useSession().locked('crm.write');
  const [locale] = useLocalesById(profile.currentLocaleId ? [profile.currentLocaleId] : []);

  return (
    <div>
      <div className="mb-[18px] grid grid-cols-2 gap-2.5 desk:grid-cols-5">
        <Stat label="Published" value={formatCount(computed?.totalPublished)} />
        <Stat label="Sold" value={formatCount(computed?.totalSold)} hint="portions" />
        <Stat
          label="Avg. rating"
          value={computed ? <RatingCell rating={computed.avgRating} /> : '—'}
          hint={computed ? `${formatCount(computed.reviewCount)} reviews` : undefined}
        />
        <Stat
          label="Last published"
          value={daysAgo(computed?.lastActiveAt)}
          hint={computed?.lastActiveAt ? formatDate(computed.lastActiveAt) : undefined}
        />
        <Stat label="Balance" value={formatCents(profile.listerBalanceCents)} hint="owed to chef" />
      </div>

      <div className="grid grid-cols-1 gap-3.5 desk:grid-cols-2">
        <div>
          <SectionLabel>Locale</SectionLabel>
          <div className="text-sm">
            {!profile.currentLocaleId ? (
              <span className="text-muted">Not set</span>
            ) : locale?.data ? (
              localeLabel(locale.data)
            ) : (
              <span className="font-mono text-xs text-muted">{profile.currentLocaleId}</span>
            )}
          </div>
          <p className="m-0 mt-1.5 text-xs text-muted">Where the chef is now. Each dish carries its own locale.</p>
        </div>
        <div>
          <SectionLabel>Blue Star status</SectionLabel>
          <Select
            aria-label="Blue Star status"
            compact
            surface="white"
            value={profile.blueStarStatus}
            // Staff act on an application; they don't start one. Until the chef
            // applies (status pending) there is nothing to decide.
            disabled={profile.blueStarStatus === 'none' || setBlueStar.isPending || !!lock}
            options={BLUE_STAR_STATUSES.map((s) => ({ value: s, label: BLUE_STAR_LABEL[s] }))}
            onChange={(e) =>
              setBlueStar.mutate(e.target.value as BlueStarStatus, {
                onSuccess: () => notify.success('Blue Star status updated.'),
              })
            }
          />
          {profile.blueStarStatus === 'none' ? (
            <p className="m-0 mt-1.5 text-xs text-muted">Editable once the chef applies (Pending).</p>
          ) : (
            lock && <p className="m-0 mt-1.5 text-xs text-muted">{lock}.</p>
          )}
        </div>
      </div>

      <div className="mt-3.5 grid grid-cols-1 gap-3.5 text-sm desk:grid-cols-2">
        <div>
          <SectionLabel>Email</SectionLabel>
          {profile.email || <span className="text-muted">—</span>}
        </div>
        <div>
          <SectionLabel>Phone</SectionLabel>
          {profile.phoneE164 || <span className="text-muted">—</span>}
        </div>
      </div>

      <ReviewsReceived profileId={profile.id} as="chef" />
    </div>
  );
}

const DISH_COLUMNS: DataTableColumn<ChefListing>[] = [
  {
    id: 'dish',
    header: 'Dish',
    width: '1.7fr',
    cell: (r) => <span className="truncate font-semibold">{r.title || 'Untitled'}</span>,
  },
  {
    id: 'status',
    header: 'Status',
    width: '112px',
    cell: (r) => {
      const s = dishStatus(r.dishStatus);
      return <StatusPill tone={s.tone}>{s.label}</StatusPill>;
    },
  },
  {
    id: 'published',
    header: 'Published',
    width: '1.2fr',
    cell: (r) => <span className="text-muted">{formatDate(r.publishedAt ?? r.createdAt)}</span>,
  },
  { id: 'price', header: 'Price', width: '80px', align: 'right', numeric: true, cell: (r) => formatCents(r.price) },
  { id: 'orders', header: 'Orders', width: '72px', align: 'right', numeric: true, cell: (r) => r.ordersPlaced },
  { id: 'sold', header: 'Sold', width: '64px', align: 'right', numeric: true, cell: (r) => r.portionsSold },
];

function DishesTab({ id }: { id: string }) {
  const [page, setPage] = useState(1);
  const listings = useChefListings(id, { page, pageSize: 10 });
  const { openDish } = useDetailLinks();
  return (
    <div>
      <DataTable
        label="Dishes"
        compact
        minWidth={0}
        columns={DISH_COLUMNS}
        rows={listings.data?.items ?? []}
        getRowId={(r) => r.listingId}
        onRowClick={(r) => openDish(r.listingId)}
        loading={listings.isPending}
        error={listings.error}
        onRetry={() => void listings.refetch()}
        emptyLabel="This chef has not published a dish yet."
      />
      {listings.data && listings.data.total > 10 && (
        <Pager page={page} pageSize={10} total={listings.data.total} onPageChange={setPage} noun="dishes" />
      )}
    </div>
  );
}
